import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { snakeCase } from 'lodash';
import { EAdvancedSettings, IAdvancedSettingRule } from './deliverable-advanced-settings.interface';

@Injectable({ providedIn: 'root' })
export class DeliverableAdvancedSettingsService {
  private readonly rules: Record<EAdvancedSettings, IAdvancedSettingRule> = {
    [EAdvancedSettings.sponsoredContentRelevant]: {
      defaultValue: true,
      excludedDeliverableTypes: [EDeliverableType.audioTracking, EDeliverableType.textTracking],
      platformFeatures: [EPlatformFeature.twitchStream, EPlatformFeature.youtubeStream],
    },
  };

  public getAdvancedSettings(
    platformFeature: EPlatformFeature | null | undefined,
    deliverableType: EDeliverableType,
  ): EAdvancedSettings[] {
    if (!platformFeature) {
      return [];
    }

    return (Object.entries(this.rules) as [EAdvancedSettings, IAdvancedSettingRule][])
      .filter(([, rule]) => {
        if (!rule.platformFeatures.includes(platformFeature)) {
          return false;
        }

        if (rule.excludedDeliverableTypes?.includes(deliverableType)) {
          return false;
        }

        return true;
      })
      .map(([setting]) => setting);
  }

  public hasAdvancedSetting(
    platformFeature: EPlatformFeature | null | undefined,
    deliverableType: EDeliverableType,
    setting: EAdvancedSettings,
  ): boolean {
    return this.getAdvancedSettings(platformFeature, deliverableType).includes(setting);
  }

  public hasAnyAdvancedSettings(
    platformFeature: EPlatformFeature | null | undefined,
    deliverableType: EDeliverableType,
  ): boolean {
    return this.getAdvancedSettings(platformFeature, deliverableType).length > 0;
  }

  public shouldAutoOpenAdvancedSettings(
    platformFeature: EPlatformFeature | null | undefined,
    deliverableType: EDeliverableType,
    form: FormGroup,
  ): boolean {
    const applicableSettings = this.getAdvancedSettings(platformFeature, deliverableType);

    if (!applicableSettings.length) {
      return false;
    }

    const globalCtaValues = form.get('globalCtaValues')?.value as IGlobalCtaValue[] | null | undefined;
    const customCtaValues = form.get('customCtaValues')?.value as ICustomCtaValue[] | null | undefined;

    return applicableSettings.some((setting) => {
      const rule = this.rules[setting];
      const valueName = snakeCase(setting);

      const globalEntry = globalCtaValues?.find((v) => v.valueName === valueName);

      if (globalEntry !== undefined && String(globalEntry.value) !== String(rule.defaultValue)) {
        return true;
      }

      return customCtaValues?.some((v) => v.valueName === valueName) ?? false;
    });
  }
}
